import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import posthog from 'posthog-js';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';

// Analytics context
const AnalyticsContext = createContext(null);

// Check if PostHog is configured
const isPostHogConfigured = POSTHOG_KEY && POSTHOG_KEY !== 'your_posthog_api_key_here';

// Track if PostHog has been initialized globally (prevents re-init on re-renders)
let posthogInitialized = false;

export const AnalyticsProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [eventCount, setEventCount] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);
  const initRef = useRef(false);

  // Initialize PostHog - only once
  useEffect(() => {
    // Check multiple conditions to prevent re-initialization
    if (initRef.current || posthogInitialized) {
      console.log('PostHog already initialized - skipping');
      setIsInitialized(true);
      return;
    }

    // Also check if window.posthog is already set up
    if (typeof window !== 'undefined' && window.posthog && window.posthog.__loaded) {
      console.log('PostHog already loaded in window - skipping init');
      setIsInitialized(true);
      posthogInitialized = true;
      initRef.current = true;
      return;
    }

    if (isPostHogConfigured) {
      initRef.current = true;
      posthogInitialized = true;

      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        persistence: 'localStorage',
        loaded: (ph) => {
          console.log('PostHog initialized successfully');
        },
        on_xhr_error: (err) => {
          console.warn('PostHog XHR error:', err);
        },
      });

      // Override capture to also send to our backend
      const originalCapture = posthog.capture.bind(posthog);
      posthog.capture = (eventName, properties) => {
        // Call original PostHog capture
        originalCapture(eventName, properties);
        
        // Mirror to our backend
        mirrorEventToBackend(eventName, properties);
        
        // Update local state for status indicator
        setEventCount(prev => prev + 1);
        setLastEvent({ name: eventName, timestamp: new Date() });
      };

      setIsInitialized(true);
    } else {
      console.log('PostHog not configured - events will only be logged to backend');
      setIsInitialized(false);
    }

    // Cleanup - but don't reset the global flag
    return () => {
      // Don't reset posthog on unmount to prevent issues
    };
  }, []); // Empty dependency array - only run once

  // Mirror event to backend UserInteraction table
  const mirrorEventToBackend = async (eventName, properties = {}) => {
    const token = localStorage.getItem('token');
    if (!token) return; // Only mirror for authenticated users

    try {
      // Map PostHog event to our interaction format
      const actionMap = {
        '$pageview': 'page_view',
        '$pageleave': 'page_view',
        '$autocapture': 'click',
        'click': 'click',
        'search': 'search',
        'save': 'save',
        'share': 'share',
        'like': 'like',
      };

      const action = actionMap[eventName] || 'click';
      const category = properties?.$current_url?.includes('/trend/') ? 'Trend Detail' :
                       properties?.$current_url?.includes('/explore') ? 'Explore' :
                       properties?.$current_url?.includes('/profile') ? 'Profile' :
                       properties?.$current_url?.includes('/affiliate') ? 'Affiliate' :
                       properties?.$current_url?.includes('/payment') ? 'Payment' :
                       properties?.$current_url?.includes('/settings') ? 'Settings' :
                       properties?.$current_url?.includes('/auth') ? 'Auth' :
                       'Home';

      await axios.post(
        `${API}/analytics/mirror`,
        {
          event_name: eventName,
          action: action,
          category: category,
          metadata: {
            ...properties,
            posthog_event: true,
            original_event: eventName,
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Silently fail - don't break the app if mirroring fails
      console.debug('Event mirror failed:', error.message);
    }
  };

  // Manual event tracking function
  const trackEvent = useCallback((eventName, properties = {}) => {
    if (isPostHogConfigured) {
      posthog.capture(eventName, properties);
    } else {
      // If PostHog not configured, just send to backend
      mirrorEventToBackend(eventName, properties);
      setEventCount(prev => prev + 1);
      setLastEvent({ name: eventName, timestamp: new Date() });
    }
  }, []);

  // Identify user for PostHog
  const identifyUser = useCallback((userId, traits = {}) => {
    if (isPostHogConfigured) {
      posthog.identify(userId, traits);
    }
  }, []);

  // Reset user identity (on logout)
  const resetUser = useCallback(() => {
    if (isPostHogConfigured) {
      posthog.reset();
    }
  }, []);

  const value = {
    isInitialized,
    isPostHogConfigured,
    eventCount,
    lastEvent,
    trackEvent,
    identifyUser,
    resetUser,
    posthog: isPostHogConfigured ? posthog : null,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Hook to use analytics
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }
  return context;
};

// HOC for automatic page view tracking
export const withPageTracking = (Component, pageName) => {
  return function TrackedComponent(props) {
    const { trackEvent } = useAnalytics();

    useEffect(() => {
      trackEvent('page_view', { page: pageName });
    }, [trackEvent]);

    return <Component {...props} />;
  };
};

export default AnalyticsProvider;
